import React, { useEffect, useRef, useState } from "react";
import { useFormikContext, FieldArray } from "formik";
import {
  Stack,
  TextField,
  Button,
  Select,
  MenuItem,
  Autocomplete,
  IconButton,
  Box,
  Badge,
  Typography,
} from "@mui/material";

import { Add, Clear, PlusOne } from "@mui/icons-material";
import currency from "currency.js";
import { EXTRACTION_URL } from "../../../../helpers/variables";
import { NumericFormat } from "react-number-format";

const TransactionDetails = ({ nimbleIntegration = false }) => {
  const { values, handleChange, touched, errors, handleBlur } =
    useFormikContext();

  const [purposes, setPurposes] = useState([]);

  const scrollRef = useRef();

  let { line_items, invoice_amount } = values;

  useEffect(() => {
    getResponses();
  }, []);

  async function getResponses() {
    let res = await fetch(`${EXTRACTION_URL}/get_purposes`);
    let data = await res.json();
    const updatedData = data.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
    setPurposes(updatedData);
  }

  const difference = currency(
    currency(invoice_amount).value -
      line_items?.reduce((sum, item) => sum + currency(item.amount).value, 0)
  ).value;

  return (
    <>
      <FieldArray name="line_items">
        {({ insert, remove, push }) => (
          <Stack
            gap={1}
            p={1}
            border={nimbleIntegration ? 0 : 1}
            borderColor={"primary.main"}
          >
            <Stack
              direction={"row"}
              alignItems={"center"}
              position={"sticky"}
              top={0}
              zIndex={2}
              bgcolor={"white"}
            >
              {nimbleIntegration ? (
                <Box flexGrow={1}></Box>
              ) : (
                <Typography
                  color={difference ? "error" : undefined}
                  flexGrow={1}
                >
                  {difference ? `${difference} Difference` : null}
                </Typography>
              )}

              <Button
                onClick={() => {
                  push({
                    amount: "",
                    purpose: "",
                    type: "",
                    description: "",
                  });
                  scrollRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
                }}
                variant="outlined"
                startIcon={<Add />}
                endIcon={<>{`(${line_items.length})`}</>}
              >
                Add
              </Button>
            </Stack>

            {line_items.map((line_item, index) => (
              <Stack
                direction={"row"}
                gap={1}
                key={index}
                width={"100%"}
                border={1}
                borderColor={"divider"}
                borderRadius={1}
                p={1.5}
                alignItems={"center"}
              >
                <Stack gap={1.5} width={"100%"}>
                  <Stack direction={"row"} gap={1} alignItems={"flex-start"}>
                    <Autocomplete
                      fullWidth
                      value={
                        purposes.find(
                          (i) =>
                            i.purpose.toLowerCase() ===
                            line_item.purpose.toLowerCase()
                        ) || null
                      }
                      options={purposes}
                      getOptionLabel={(option) => option.purpose}
                      // groupBy={(option) => option.type}
                      onChange={(e, newValue) => {
                        if (!newValue) return;
                        handleChange({
                          target: {
                            name: `line_items[${index}].purpose`,
                            value: newValue.purpose,
                          },
                        });
                        handleChange({
                          target: {
                            name: `line_items[${index}].type`,
                            value: newValue.type,
                          },
                        });
                      }}
                      onBlur={handleBlur}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} key={option.id}>
                          <span>{option.purpose}</span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Purposes"
                          variant="outlined"
                          size="small"
                          required
                          error={
                            touched.line_items?.[index]?.purpose &&
                            Boolean(errors.line_items?.[index]?.purpose)
                          }
                          helperText={
                            touched.line_items?.[index]?.purpose &&
                            errors.line_items?.[index]?.purpose
                          }
                        />
                      )}
                    />
                    {nimbleIntegration && (
                      <TextField
                        name={`line_items[${index}].description`}
                        value={line_item.description}
                        onChange={handleChange}
                        label="Description"
                        size="small"
                        fullWidth
                        onBlur={handleBlur}
                        error={
                          touched.line_items?.[index]?.description &&
                          Boolean(errors.line_items?.[index]?.description)
                        }
                        helperText={
                          touched.line_items?.[index]?.description &&
                          errors.line_items?.[index]?.description
                        }
                      />
                    )}

                    <NumericFormat
                      onValueChange={(v, _) => {
                        console.log(v);
                        handleChange({
                          target: {
                            name: `line_items[${index}].amount`,
                            value: v.floatValue,
                          },
                        });
                      }}
                      customInput={TextField}
                      name={`line_items[${index}].amount`}
                      value={line_item.amount}
                      label="Amount"
                      size="small"
                      required
                      onBlur={handleBlur}
                      error={
                        touched.line_items?.[index]?.amount &&
                        Boolean(errors.line_items?.[index]?.amount)
                      }
                      helperText={
                        touched.line_items?.[index]?.amount &&
                        errors.line_items?.[index]?.amount
                      }
                    />
                  </Stack>

                  {!nimbleIntegration && (
                    <TextField
                      name={`line_items[${index}].description`}
                      value={line_item.description}
                      onChange={handleChange}
                      label="Description"
                      size="small"
                      fullWidth
                      onBlur={handleBlur}
                      error={
                        touched.line_items?.[index]?.description &&
                        Boolean(errors.line_items?.[index]?.description)
                      }
                      helperText={
                        touched.line_items?.[index]?.description &&
                        errors.line_items?.[index]?.description
                      }
                    />
                  )}
                </Stack>

                <Box>
                  <IconButton color="error" onClick={() => remove(index)}>
                    <Clear />
                  </IconButton>
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </FieldArray>
      <div ref={scrollRef} />
    </>
  );
};

export default TransactionDetails;
